import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "../lib/AppContext.tsx";
import { Skeleton } from "../components/ui/skeleton.tsx";
import { Card } from "../components/ui/card.tsx";
import { Badge } from "../components/ui/badge.tsx";
import { Button } from "../components/ui/button.tsx";
import { clientMessageSchema } from "../../../shared/schema";
import { format } from "date-fns";
import { MessageSquare } from "lucide-react";
import { Pencil, MoreVertical, Archive, Trash2, ArchiveRestore, MessageSquarePlus } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar.tsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu.tsx";
import { BottomToaster } from "../components/ui/bottom-toaster.tsx";
import { toast } from "../components/ui/use-toast.ts";
import { useEffect, useState, useMemo, useRef, TouchEvent } from "react";
import { useLocation } from "wouter";
import { supabase } from "../lib/supabase";

interface Conversation {
  userId: number;
  unreadCount: number;
  lastMessage: {
    id: number;
    content: string;
    createdAt: string;
    senderId: number;
    receiverId: number;
  };
  user: {
    id: number;
    name: string;
    avatar?: string;
    role: string;
  } | null;
}

export default function Messages() {
  const { currentUser, isGuest, openAuthPrompt } = useAppContext();
  const [location, setLocation] = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hiddenConversations, setHiddenConversations] = useState<string[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<string[]>([]);
  const [hiddenMeta, setHiddenMeta] = useState<Record<string, number>>({});
  const [archiveMeta, setArchiveMeta] = useState<Record<string, number>>({});

  // Load archived conversations from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('archivedConversations');
      if (raw) {
        const ids = JSON.parse(raw);
        if (Array.isArray(ids)) setArchivedConversations(ids.map(String));
      }
      const metaRaw = localStorage.getItem('archivedConversationsMeta');
      if (metaRaw) {
        const map = JSON.parse(metaRaw);
        if (map && typeof map === 'object') setArchiveMeta(map);
      }
    } catch {}
  }, []);

  // Load deleted/hidden conversations from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('hiddenConversations');
      if (raw) {
        const ids = JSON.parse(raw);
        if (Array.isArray(ids)) setHiddenConversations(ids.map(String));
      }
      const metaRaw = localStorage.getItem('hiddenConversationsMeta');
      if (metaRaw) {
        const map = JSON.parse(metaRaw);
        if (map && typeof map === 'object') setHiddenMeta(map);
      }
    } catch {}
  }, []);

  const handleDeleteConversation = (otherUserId: string) => {
    setHiddenConversations(prev => {
      const next = Array.from(new Set([...prev, String(otherUserId)]));
      try { localStorage.setItem('hiddenConversations', JSON.stringify(next)); } catch {}
      return next;
    });
    setHiddenMeta(prev => {
      const next = { ...prev, [String(otherUserId)]: Date.now() };
      try { localStorage.setItem('hiddenConversationsMeta', JSON.stringify(next)); } catch {}
      return next;
    });
    let dismissToast: (() => void) | null = null;
    const t = toast({
      title: 'Conversation deleted',
      action: (
        <button
          className="ml-4 text-blue-600 font-medium"
          onClick={(e) => {
            e.stopPropagation();
            setHiddenConversations(prev => {
              const next = prev.filter(id => id !== String(otherUserId));
              try { localStorage.setItem('hiddenConversations', JSON.stringify(next)); } catch {}
              return next;
            });
            setHiddenMeta(prev => {
              const next = { ...prev };
              delete next[String(otherUserId)];
              try { localStorage.setItem('hiddenConversationsMeta', JSON.stringify(next)); } catch {}
              return next;
            });
            dismissToast?.();
          }}
        >
          Undo
        </button>
      ),
    });
    dismissToast = t.dismiss;
    setTimeout(() => {
      t.dismiss();
    }, 5000);
  };

  // Scroll to top of the messages container on mount
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, []);

  // Parse ?user=USER_ID from the URL and redirect if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get("user");
    const from = params.get("from");
    if (userId) {
      const qs = from ? `?from=${encodeURIComponent(from)}` : "";
      setLocation(`/messages/${userId}${qs}`);
    }
  }, [setLocation]);

  // Fetch all messages involving the current user
  const { data: messages, isLoading } = useQuery({
    queryKey: ["all-messages", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .or(`sender_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 3000,
    staleTime: 0,
  });

  // Group messages by the other user and get the latest message for each conversation
  const conversations = useMemo(() => {
    if (!messages || !currentUser?.id) return [];
    const convoMap = new Map();
    for (const msg of messages) {
      const otherUserId = msg.sender_id === currentUser.id ? msg.receiver_id : msg.sender_id;
      if (!convoMap.has(otherUserId)) {
        convoMap.set(otherUserId, msg);
      }
    }
    return Array.from(convoMap.values());
  }, [messages, currentUser?.id]);

  // Fetch user info for all conversation partners
  const [userInfos, setUserInfos] = useState<{ [key: string]: any }>({});
  useEffect(() => {
    const fetchUsers = async () => {
      const ids = conversations.map((msg: any) =>
        msg.sender_id === currentUser?.id ? msg.receiver_id : msg.sender_id
      );
      if (ids.length === 0) return;
      const { data, error } = await supabase
        .from("users")
        .select("id, name, avatar")
        .in("id", ids);
      if (!error && data) {
        const infoMap: { [key: string]: any } = {};
        for (const user of data) {
          infoMap[user.id] = user;
        }
        setUserInfos(infoMap);
      }
    };
    fetchUsers();
  }, [conversations, currentUser?.id]);

  // Filter out hidden and archived conversations
  const visibleConversations = conversations.filter((msg: any) => {
    const otherUserId = msg.sender_id === currentUser?.id ? msg.receiver_id : msg.sender_id;
    return !hiddenConversations.includes(String(otherUserId)) && !archivedConversations.includes(String(otherUserId));
  });

  // Unhide/unarchive conversations when new messages arrive
  useEffect(() => {
    try {
      if (!messages || !Array.isArray(messages)) return;
      const latestByUser: Record<string, number> = {};
      for (const msg of messages as any[]) {
        const otherUserId = msg.sender_id === currentUser?.id ? msg.receiver_id : msg.sender_id;
        if (!otherUserId) continue;
        const createdAt = new Date(msg.created_at).getTime();
        const key = String(otherUserId);
        if (!latestByUser[key] || createdAt > latestByUser[key]) {
          latestByUser[key] = createdAt;
        }
      }
      const toUnhide: string[] = [];
      for (const id of hiddenConversations) {
        const hiddenAt = hiddenMeta[id];
        const latestAt = latestByUser[id];
        // Unhide if there is newer activity OR if meta is missing but messages exist (legacy entries)
        if ((hiddenAt && latestAt && latestAt > hiddenAt) || (!hiddenAt && latestAt)) {
          toUnhide.push(id);
        }
      }
      if (toUnhide.length > 0) {
        const nextHidden = hiddenConversations.filter(id => !toUnhide.includes(id));
        setHiddenConversations(nextHidden);
        try { localStorage.setItem('hiddenConversations', JSON.stringify(nextHidden)); } catch {}
        const nextMeta = { ...hiddenMeta };
        toUnhide.forEach(id => delete nextMeta[id]);
        setHiddenMeta(nextMeta);
        try { localStorage.setItem('hiddenConversationsMeta', JSON.stringify(nextMeta)); } catch {}
      }

      const toUnarchive: string[] = [];
      for (const id of archivedConversations) {
        const archivedAt = archiveMeta[id];
        const latestAt = latestByUser[id];
        if ((archivedAt && latestAt && latestAt > archivedAt) || (!archivedAt && latestAt)) {
          toUnarchive.push(id);
        }
      }
      if (toUnarchive.length > 0) {
        const nextArchived = archivedConversations.filter(id => !toUnarchive.includes(id));
        setArchivedConversations(nextArchived);
        try { localStorage.setItem('archivedConversations', JSON.stringify(nextArchived)); } catch {}
        const nextArchiveMeta = { ...archiveMeta };
        toUnarchive.forEach(id => delete nextArchiveMeta[id]);
        setArchiveMeta(nextArchiveMeta);
        try { localStorage.setItem('archivedConversationsMeta', JSON.stringify(nextArchiveMeta)); } catch {}
      }
    } catch {}
  }, [messages, hiddenConversations, archivedConversations, hiddenMeta, archiveMeta, currentUser?.id]);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 overflow-x-hidden">
      <div className="blurred-bg" />
      <div
        ref={containerRef}
        className="relative z-10 px-4 py-8 max-w-2xl mx-auto flex flex-col gap-8 mt-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold mb-6 text-blue-700">Messages</h1>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="gradient-btn rounded-full"
              onClick={() => {
                if (isGuest) {
                  openAuthPrompt('Create an account or sign in to view and manage messages.');
                  return;
                }
                setLocation("/messages/archived")
              }}
            >
              <ArchiveRestore className="h-4 w-4 mr-2" />
              Archive
            </Button>
            <Button
              size="sm"
              className="gradient-btn rounded-full"
              onClick={() => {
                if (isGuest) {
                  openAuthPrompt('Create an account or sign in to start new conversations.');
                  return;
                }
                setLocation("/friends")
              }}
            >
              <MessageSquarePlus className="h-4 w-4 mr-2" />
              New
            </Button>
          </div>
        </div>
        {isLoading ? (
          <>
            <Skeleton className="h-20 w-full rounded-xl mb-3" />
            <Skeleton className="h-20 w-full rounded-xl mb-3" />
            <Skeleton className="h-20 w-full rounded-xl mb-3" />
          </>
        ) : visibleConversations && visibleConversations.length > 0 ? (
          visibleConversations.map((msg: any) => {
            const otherUserId = msg.sender_id === currentUser?.id ? msg.receiver_id : msg.sender_id;
            const user = userInfos[otherUserId];
            const messageDate = new Date(msg.created_at);
            const today = new Date();
            const formattedDate =
              messageDate.toDateString() === today.toDateString()
                ? format(messageDate, "'Today at' h:mm a")
                : format(messageDate, "MMM d, yyyy");
            return (
              <div
                key={otherUserId}
                className="relative h-[72px] mb-3"
              >
                <Card
                  className="glass-card deep-shadow p-3 flex items-center transition-transform duration-200 h-full"
                  onClick={() => {
                    if (isGuest) {
                      openAuthPrompt('Create an account or sign in to view and send messages.');
                      return;
                    }
                    setLocation(`/messages/${otherUserId}`)
                  }}
                  style={{ minHeight: '100%' }}
                >
                  <Avatar className="h-12 w-12">
                    {user?.avatar ? (
                      <AvatarImage src={user.avatar} alt={user.name} />
                    ) : null}
                    <AvatarFallback>{user?.name?.split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium text-gray-900 truncate">{user?.name || 'User'}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{formattedDate}</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="p-1 hover:bg-gray-100 rounded"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4 text-gray-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-0 w-[6.5rem]">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setArchivedConversations(prev => {
                                  const next = Array.from(new Set([...prev, String(otherUserId)]));
                                  try { localStorage.setItem('archivedConversations', JSON.stringify(next)); } catch {}
                                  return next;
                                });
                                setArchiveMeta(prev => {
                                  const next = { ...prev, [String(otherUserId)]: Date.now() };
                                  try { localStorage.setItem('archivedConversationsMeta', JSON.stringify(next)); } catch {}
                                  return next;
                                });
                              }}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConversation(String(otherUserId));
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">
                        {msg.content}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            );
          })
        ) : (
          <div className="glass-card deep-shadow p-8 rounded-2xl text-center">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-900">No Messages</h3>
            <p className="text-gray-500 mt-1">
              You don't have any conversations yet.
            </p>
          </div>
        )}
      </div>
      {/* Bottom-positioned toaster so it sits above bottom nav */}
      <BottomToaster />
    </div>
  );
}